import React, { useEffect, useState, useCallback } from "react";
import CustomTable from "src/components/App/AppGrid/CustomTable/CustomTable";
import { Avatar, Chip, Box, alpha, LinearProgress, Card, CardContent, Typography, IconButton, Collapse } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PendingIcon from "@mui/icons-material/Pending";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import theme from "src/config/theme";
import ButtonComponent from "src/components/shared/Button";
import DialogBox from "src/components/shared/Dialog/DialogBox";
import { ReactComponent as Edit } from '../../../../../assets/images/edit-2.svg';
import { ReactComponent as Delete } from '../../../../../assets/images/trash.svg';
import { ReactComponent as Add } from '../../../../../assets/images/add-square.svg';
import PaymentTabForm from "../Forms/PaymentTabForm";
import NoDataFound from "../../NoDataFound/NoDataFound";
import { useDispatch, useSelector } from "react-redux";
import { getBillingsByPatientIdRequest } from "src/modules/app/store/appActions";
import useResponsive from "src/components/hooks/useResponsive";
import useAppointmentDetails from "../../UseAppointmentDetails";
import { AllTabsMainContainer } from "../Style";
import PaymentCard from "../CardView/PaymentCard";

import dayjs from "dayjs";
import RefundForm from "../Forms/RefundForm";

const columns = [
  { id: "paymentfor", label: "Payment For", sx: { width: "35%", minWidth: "180px", textAlign: 'left' } },
  { id: "amount", label: "Amount", sx: { textAlign: 'center', minWidth: "120px" } },
  { id: "addedDate", label: "Date", sx: { textAlign: 'center', minWidth: "120px" } },
  { id: "paymenttype", label: "Type", sx: { textAlign: 'center', minWidth: "130px" } },
  { id: "paymentmethod", label: "Method", sx: { textAlign: 'center', minWidth: "120px" } }
];

const Payment = ({ shouldRefresh }) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [openPayment, setOpenPayment] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);
  const [openOutstanding, setOpenOutstanding] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const dispatch = useDispatch();
  const [payment, setPayment] = useState([]);
  const { PatientId } = useAppointmentDetails();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const Width = isMobile || isTablet ? 18 : 24;
  const Height = isMobile || isTablet ? 18 : 24;

  const handleRefundOpen = (paymentId, paymenttype) => {
    setSelectedPaymentId(paymentId);
    setSelectedPaymentType(paymenttype);
    setOpenPayment(true);
  };
  const handleRefundClose = () => {
    setSelectedPaymentId(null);
    setSelectedPaymentType(null);
    setOpenPayment(false);
  };
  const handleOutstandingOpen = () => setOpenOutstanding(true);
  const handleOutstandingClose = () => setOpenOutstanding(false);

  const fetchPayments = useCallback(
    (page = 0, perPage = 10) => {
      const fetchData = async () => {
        const params = {
          page: page + 1,
          limit: perPage,
        };

        const response = await new Promise((resolve, reject) => {
          dispatch(getBillingsByPatientIdRequest({ patientId: PatientId, params }, resolve, reject));
        });

        if (response?.data && response?.meta) {
          setPayment(response.data);
          setTotalCount(response.meta.totalRecords);
        } else {
          console.log('Error fetching Payments');
        }
      };
      fetchData();
    },
    [dispatch, PatientId],
  );

  useEffect(() => {
    if (PatientId) {
      fetchPayments(page, perPage);
    }
  }, [PatientId, fetchPayments, page, perPage, shouldRefresh]);

  useEffect(() => {
    const handleBillingCreated = () => {
      fetchPayments(page, perPage);
    };
    window.addEventListener('app:billingCreated', handleBillingCreated);
    return () => {
      window.removeEventListener('app:billingCreated', handleBillingCreated);
    };
  }, [fetchPayments, page, perPage]);

  const actions = (row) => {
    if (!row) return [];

    return row.paymenttypeRaw === 'credit'
      ? [
        {
          label: "Refund",
          icon: <Edit width={Width} height={Height} />,
          onClick: () => handleRefundOpen(row.paymentId, row.paymenttypeRaw),
        }
      ]
      : [];
  };

  const data = payment.map((paymentData) => {
    const isRefund = paymentData.amount < 0 || String(paymentData.paymentFor).toLowerCase() === 'refund';
    const isCredit = paymentData.type === 'credit';
    const absAmount = Math.abs(Number(paymentData.amount) || 0);

    return {
      paymentId: paymentData.id || paymentData._id,
      paymenttypeRaw: paymentData.type,
      paymentfor: (
        <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
            {paymentData.paymentFor || 'Consultation Fee'}
          </Typography>
          {paymentData.referenceId && (
            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '11px', mt: 0.2 }}>
              Ref: {paymentData.referenceId}
            </Typography>
          )}
        </Box>
      ),
      amount: (
        <Box sx={{ color: isCredit ? '#16a34a' : '#2563eb', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>
          {isCredit ? `+ ₹${absAmount.toLocaleString('en-IN')}` : `₹${absAmount.toLocaleString('en-IN')}`}
        </Box>
      ),
      addedDate: (
        <Typography variant="body2" sx={{ color: '#475569', textAlign: 'center' }}>
          {dayjs(paymentData.date).format("DD MMM YYYY")}
        </Typography>
      ),
      paymenttype: (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Chip
            size="small"
            label={isCredit ? "Paid / Credit" : "Charge / Debit"}
            sx={{
              fontWeight: 600,
              fontSize: '11px',
              textTransform: 'uppercase',
              bgcolor: isCredit ? '#dcfce7' : '#e0f2fe',
              color: isCredit ? '#15803d' : '#0369a1',
              borderRadius: '6px',
              height: '24px'
            }}
          />
        </Box>
      ),
      paymentmethod: (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Chip
            size="small"
            label={(paymentData.method || "Cash").toUpperCase()}
            variant="outlined"
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#475569',
              borderColor: '#cbd5e1',
              borderRadius: '6px',
              height: '24px'
            }}
          />
        </Box>
      ),
      disableActions: !isCredit,
    };
  });

  const sessions = (() => {
    const map = {};
    payment.forEach((p) => {
      const sid = p.tokenAppointment || 'unknown';
      if (!map[sid]) {
        map[sid] = { charges: 0, credits: 0, refunds: 0, entries: [] };
      }
      if (p.type === 'credit') {
        map[sid].credits += Math.abs(Number(p.amount) || 0);
      } else if (p.type === 'debit') {
        if (String(p.paymentFor).toLowerCase() === 'refund') {
          map[sid].refunds += Math.abs(Number(p.amount) || 0);
        } else {
          map[sid].charges += Math.abs(Number(p.amount) || 0);
        }
      }
      map[sid].entries.push(p);
    });
    return map;
  })();

  const overallCharges = Object.values(sessions).reduce((sum, s) => sum + s.charges, 0);
  const overallCredits = Object.values(sessions).reduce((sum, s) => sum + s.credits, 0);
  const overallRefunds = Object.values(sessions).reduce((sum, s) => sum + s.refunds, 0);
  const overallNetPaid = Math.max(overallCredits - overallRefunds, 0);
  const overallRemaining = Math.max(overallCharges - overallNetPaid, 0);
  const overallPaid = overallCredits;
  const overallPercent = overallCharges > 0 ? Math.min(100, Math.round((overallNetPaid / overallCharges) * 100)) : (overallPaid > 0 ? 100 : 0);

  const fmt = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;

  return (
    <>
      <AllTabsMainContainer sx={{ justifyContent: 'flex-start', height: 'auto', gap: 2.5 }}>
        {/* ================= BILLING SUMMARY CARDS ================= */}
        <Box
          sx={{
            background: "#fff",
            borderRadius: 3,
            p: 2.5,
            boxShadow: "0 2px 8px -2px rgba(0,0,0,0.06), 0 1px 4px -1px rgba(0,0,0,0.04)",
            border: "1px solid #e2e8f0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ReceiptLongIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", fontSize: "17px" }}>
                Billing & Payment Overview
              </Typography>
            </Box>
            {overallRemaining > 0 ? (
              <Chip
                icon={<HourglassEmptyIcon sx={{ fontSize: '14px !important', color: '#b45309 !important' }} />}
                label={`Due: ${fmt(overallRemaining)}`}
                sx={{
                  bgcolor: '#fef3c7',
                  color: '#b45309',
                  fontWeight: 700,
                  fontSize: '12px',
                  borderRadius: '8px',
                  border: '1px solid #fde68a'
                }}
              />
            ) : overallCharges > 0 ? (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: '#15803d !important' }} />}
                label="Fully Settled"
                sx={{
                  bgcolor: '#dcfce7',
                  color: '#15803d',
                  fontWeight: 700,
                  fontSize: '12px',
                  borderRadius: '8px',
                  border: '1px solid #bbf7d0'
                }}
              />
            ) : (
              <Chip
                label="No Charges"
                size="small"
                sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 600, fontSize: '12px' }}
              />
            )}
          </Box>

          {/* 4 Metric Cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
              gap: 2,
              mb: 2.5,
            }}
          >
            {/* Total Charges */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2.5,
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }
              }}
            >
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600, fontSize: "13px" }}>
                Total Charges
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                {fmt(overallCharges)}
              </Typography>
              <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "11px" }}>
                Invoiced & OPD fees
              </Typography>
            </Box>

            {/* Total Paid */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2.5,
                bgcolor: "#f0fdf4",
                border: "1px solid #bbf7d0",
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }
              }}
            >
              <Typography variant="body2" sx={{ color: "#166534", fontWeight: 600, fontSize: "13px" }}>
                Total Paid
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#15803d" }}>
                {fmt(overallPaid)}
              </Typography>
              <Typography variant="caption" sx={{ color: "#22c55e", fontSize: "11px", fontWeight: 600 }}>
                ✓ Collected Amount
              </Typography>
            </Box>

            {/* Refunds */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2.5,
                bgcolor: "#fef2f2",
                border: "1px solid #fecdd3",
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }
              }}
            >
              <Typography variant="body2" sx={{ color: "#991b1b", fontWeight: 600, fontSize: "13px" }}>
                Refunds
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#b91c1c" }}>
                {fmt(overallRefunds)}
              </Typography>
              <Typography variant="caption" sx={{ color: "#f87171", fontSize: "11px" }}>
                Returned to Patient
              </Typography>
            </Box>

            {/* Outstanding */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2.5,
                bgcolor: overallRemaining > 0 ? "#fffbeb" : "#f8fafc",
                border: overallRemaining > 0 ? "1px solid #fde68a" : "1px solid #e2e8f0",
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }
              }}
            >
              <Typography variant="body2" sx={{ color: overallRemaining > 0 ? "#92400e" : "#64748b", fontWeight: 600, fontSize: "13px" }}>
                Outstanding Due
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: overallRemaining > 0 ? "#b45309" : "#334155" }}>
                {fmt(overallRemaining)}
              </Typography>
              <Typography variant="caption" sx={{ color: overallRemaining > 0 ? "#d97706" : "#94a3b8", fontSize: "11px", fontWeight: 500 }}>
                {overallRemaining > 0 ? "Pending Collection" : "No Pending Dues"}
              </Typography>
            </Box>
          </Box>

          {/* Progress / Settlement Bar */}
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                Collection Rate
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: overallPercent === 100 ? "#15803d" : "#0284c7" }}>
                {overallPercent}% Settled
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={overallPercent}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: "#e2e8f0",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 4,
                  bgcolor: overallPercent === 100 ? "#22c55e" : theme.palette.primary.main,
                }
              }}
            />
          </Box>
        </Box>

        {/* ================= TRANSACTION HISTORY TABLE ================= */}
        <Box
          sx={{
            background: "#fff",
            borderRadius: 3,
            p: 2.5,
            boxShadow: "0 2px 8px -2px rgba(0,0,0,0.06), 0 1px 4px -1px rgba(0,0,0,0.04)",
            border: "1px solid #e2e8f0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", fontSize: "17px" }}>
                Transaction History
              </Typography>
              {payment.length > 0 && (
                <Chip
                  size="small"
                  label={`${totalCount || payment.length} ${(totalCount || payment.length) === 1 ? 'Record' : 'Records'}`}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    fontSize: '11px',
                    height: '24px',
                    borderRadius: '6px'
                  }}
                />
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <ButtonComponent
                variant="contained"
                onClick={handleOpen}
                startIcon={<Add width={18} height={18} />}
                sx={{
                  minWidth: "140px",
                  height: "36px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 600,
                  bgcolor: theme.palette.primary.main,
                  "&:hover": { bgcolor: theme.palette.primary.dark }
                }}
              >
                Add Payment
              </ButtonComponent>

              <IconButton
                onClick={() => setShowHistory((v) => !v)}
                size="small"
                sx={{
                  transform: showHistory ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px"
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Collapse in={showHistory}>
            <CustomTable
              key={page}
              columns={columns}
              data={data}
              actions={actions}
              page={page}
              onPageChange={(newPage) => setPage(newPage)}
              onPerPageChange={(newPerPage) => {
                setPerPage(newPerPage);
                setPage(0);
              }}
              perPage={perPage}
              totalCount={totalCount}
              noDataComponent={
                <NoDataFound
                  title="No Payments Found!"
                  description="No billing or transaction records found for this patient."
                  buttonText="Add First Payment"
                  buttonIcon={Add}
                  onClick={handleOpen}
                />
              }
            />
          </Collapse>
        </Box>

        {/* ================= DIALOGS ================= */}
        <DialogBox open={open} onClose={handleClose} title="Add Payment">
          <PaymentTabForm
            onClose={handleClose}
            fetchPayments={fetchPayments}
            defaultCollectAmount={overallRemaining}
          />
        </DialogBox>

        <DialogBox
          open={openPayment}
          onClose={handleRefundClose}
          title="Refund Payment"
        >
          <RefundForm
            open={openPayment}
            onClose={handleRefundClose}
            fetchPayments={fetchPayments}
            PaymentId={selectedPaymentId}
          />
        </DialogBox>
      </AllTabsMainContainer>
    </>
  );
};

export default Payment;
